import { useEffect, useState, useSyncExternalStore } from 'react';
import { Network } from '@capacitor/network';
import { liveQuery } from 'dexie';
import { db } from './db.js';
import { createOutbox } from './outbox.js';
import { flushPhotos } from './photos.js';
import { flushTicketFiles } from '../modules/helpdesk/files.js';
import { api } from '../api/client.js';
import { isNative } from '../config.js';

export const outbox = createOutbox({
  db,
  send: async (mutations) => (await api('/sync/mutations', { method: 'POST', body: { mutations } })).results,
});

// Try to send whenever there is a reason to think we're back online.
let started = false;
export function startSync() {
  if (started) return;
  started = true;
  const kick = () => { outbox.flush(); flushPhotos(); flushTicketFiles(); };
  window.addEventListener('online', kick);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') kick(); });
  if (isNative) Network.addListener('networkStatusChange', (s) => { if (s.connected) kick(); });
  setInterval(kick, 60_000);
  kick();
}

// A live query re-runs whenever the outbox table changes, including changes
// made by another tab of the app (office users often have several open).
// Photos still on the phone count as waiting to send.
export function useOutboxCounts() {
  const [counts, setCounts] = useState({ pending: 0, rejected: 0 });
  useEffect(() => {
    const sub = liveQuery(async () => {
      const c = await outbox.counts();
      return { ...c, pending: c.pending + await db.photos.count() + await db.ticketFiles.count() };
    }).subscribe({ next: setCounts });
    return () => sub.unsubscribe();
  }, []);
  return counts;
}

function subscribeOnline(cb) {
  window.addEventListener('online', cb);
  window.addEventListener('offline', cb);
  return () => {
    window.removeEventListener('online', cb);
    window.removeEventListener('offline', cb);
  };
}

export function useOnline() {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine);
}
